import styles from "./ShiftsRow.module.scss";
import enlargedDropZone from "./DragAndDrop/enlargedDropZone.module.scss";
import { Text } from "@medoma/components/Text/Text";
import {
  TimelineActivityTile,
  TimelineGroupTile,
  TimelineVisitTile,
} from "./TimelineTile/TimelineTile";
import { LoadingOverlay } from "@medoma/components/Loading/Loading";
import {
  employeeName,
  medicalCompetenceDictionary,
  shiftName,
  shiftTypesDictionary,
  unusualShiftTypes,
} from "@medoma/models/shifts";
import type { IScheduledShift } from "@medoma/models/shifts";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import TeamBadge from "@/components/Teams/TeamBadge";
import { AllocateToShiftTile } from "./TimelineTile/AllocateToShiftTile";
import { useIsShiftDropZoneEnlarged } from "./DragAndDrop/useIsShiftDropZoneEnlarged";
import { DropZone } from "@/components/DragAndDrop/DropZone";
import clsx from "clsx";
import {
  MedomaGridList,
  MedomaGridListItem,
} from "@/components/DragAndDrop/DragAndDrop";
import { useLingui } from "@lingui/react/macro";
import { useSelection } from "./Selection/SelectionContext";
import { useSelectionInTimeline } from "./Selection/useSelectionInTimeline";
import { useIsRouteDropZoneAvailable } from "./DragAndDrop/useIsRouteDropZoneAvailable";
import { useDropToRoute } from "./DragAndDrop/useDropToRoute";
import { useDragAndDropFromRoute } from "./DragAndDrop/useDragAndDropFromRoute";
import { useDragAndDropFromShiftLane } from "./DragAndDrop/useDragAndDropFromShiftLane";

import PinIcon from "@medoma/components/icons/PinIcon";
import { Trans } from "@lingui/react/macro";
import { ShiftTime } from "@/components/Time/ShiftTime";
import { Link } from "@medoma/components/Link/Link";
import {
  timeOfDayDictionary,
  timeOfDaySchema,
  isGroup,
  type INonRoutableEmployeePerformedActivityOccurrenceOrGroup,
} from "@medoma/models/activities";
import { useReorderVisits } from "./DragAndDrop/useReorderVisits";
import { useShouldDisablePointerEventsForRouteDropZone } from "./DragAndDrop/useShouldDisablePointerEventsForRouteDropZone";
import { activityCategoryDictionary } from "@/api/Activities";
import {
  getPatientFullName,
  patientStatusSchema,
} from "@medoma/models/patients";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import { calculateVisitTimes } from "@medoma/models/visits";
import { isRoute, type IRoute } from "@medoma/models/routes";
import {
  classifyShiftItem,
  shiftItemKindSchema,
  type IShiftWithContents,
} from "@medoma/models/shiftWithContents";

const StandaloneActivityOccurrenceOrGroup = ({
  occurrenceOrGroupItem,
  isExpanded,
  shift,
  isFilterMatch,
}: {
  occurrenceOrGroupItem: INonRoutableEmployeePerformedActivityOccurrenceOrGroup;
  isExpanded: boolean;
  shift: { id: number };
  isFilterMatch: boolean;
}) => {
  const { t } = useLingui();
  const { selectedItem } = useSelection();
  const {
    isActivityOccurrenceOrGroupSelected,
    toggleActivityOccurrenceSelection,
  } = useSelectionInTimeline();

  const { dragAndDropHooks } = useDragAndDropFromShiftLane({
    occurrenceOrGroupItem,
    shiftId: shift.id,
  });

  const categoryLabel = t(
    activityCategoryDictionary[occurrenceOrGroupItem.category],
  );
  const titleLabel = isGroup(occurrenceOrGroupItem)
    ? occurrenceOrGroupItem.occurrences
        .map((occurrence) => occurrence.title)
        .join(", ")
    : occurrenceOrGroupItem.title;
  const patientLabel =
    occurrenceOrGroupItem.patient && "name" in occurrenceOrGroupItem.patient
      ? getPatientFullName(occurrenceOrGroupItem.patient.name)
      : "";
  const timeSpanLabel =
    occurrenceOrGroupItem.timeOfDay === timeOfDaySchema.enum.Any
      ? t(timeOfDayDictionary.Any.long)
      : formattedTimeSpan(
          occurrenceOrGroupItem.start,
          occurrenceOrGroupItem.end,
        );
  const gridItemAriaLabel = [
    categoryLabel,
    titleLabel,
    patientLabel,
    timeSpanLabel,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <MedomaGridList
      key={occurrenceOrGroupItem.id}
      aria-label={t`Enskild aktivitet`}
      selectionMode="single"
      items={[occurrenceOrGroupItem]}
      dragAndDropHooks={dragAndDropHooks}
      dependencies={[isExpanded, selectedItem, isFilterMatch]}
      className={styles.standaloneActivityGrid}
    >
      {(occurrenceOrGroup) => (
        <MedomaGridListItem
          id={occurrenceOrGroup.id}
          textValue={gridItemAriaLabel}
          onAction={() => {
            toggleActivityOccurrenceSelection(occurrenceOrGroup, shift.id);
          }}
          className={clsx(
            styles.activityOccurrence,
            isFilterMatch && styles.matchHighlight,
          )}
        >
          {isGroup(occurrenceOrGroup) ? (
            <TimelineGroupTile
              group={occurrenceOrGroup}
              variant={isExpanded ? "default" : "compact"}
              isSelected={isActivityOccurrenceOrGroupSelected(
                occurrenceOrGroup,
              )}
            />
          ) : (
            <TimelineActivityTile
              activityOccurrence={occurrenceOrGroup}
              variant={isExpanded ? "default" : "compact"}
              isSelected={isActivityOccurrenceOrGroupSelected(
                occurrenceOrGroup,
              )}
            />
          )}
        </MedomaGridListItem>
      )}
    </MedomaGridList>
  );
};

const Route = ({
  routeItem,
  isExpanded,
  shift,
  matchedItemIds,
}: {
  routeItem: Pick<IRoute, "id" | "visits" | "status">;
  isExpanded: boolean;
  shift: Pick<IScheduledShift, "id" | "startDateTime" | "endDateTime">;
  matchedItemIds: Set<string>;
}) => {
  const { t } = useLingui();
  const visits = routeItem.visits;
  const routeId = routeItem.id;
  const { selectedItem } = useSelection();
  const {
    isVisitSelected,
    isRouteSelected,
    toggleRouteSelection,
    toggleVisitSelection,
  } = useSelectionInTimeline();

  const isRouteDropZoneAvailable = useIsRouteDropZoneAvailable({
    routeStatus: routeItem.status,
    routeId,
  });

  const { handleDrop, isPending } = useDropToRoute(shift.id, routeId);

  const { reorder, isReordering } = useReorderVisits({ routeId });

  const { dragAndDropHooks } = useDragAndDropFromRoute({
    routeItem,
    reorder,
    shiftId: shift.id,
  });

  const disablePointerEventsForRouteDropZone =
    useShouldDisablePointerEventsForRouteDropZone({ routeId });

  const addToRoute = t`Lägg till i rutt`;

  const addToRouteForEmployee = (
    employee: Parameters<typeof employeeName>[0],
  ) => {
    const nameOfEmployee = employeeName(employee, {
      employeeNameFormat: "full",
    });
    return t`Lägg till i rutt för ${nameOfEmployee}`;
  };

  return (
    <LoadingOverlay key={routeItem.id} show={isPending || isReordering}>
      <DropZone
        className={clsx(
          styles.route,
          styles.dropZone,
          isRouteSelected(routeItem.id) && styles.selectedRoute,
          isRouteDropZoneAvailable && styles.showExistingRoutesAsDropZones,
        )}
        ariaLabel={
          visits[0]?.assignees[0]?.employee
            ? addToRouteForEmployee(visits[0]?.assignees[0]?.employee)
            : addToRoute
        }
        onDrop={handleDrop}
        isDisabled={isReordering || !isRouteDropZoneAvailable}
      >
        <button
          type="button"
          onClick={() => toggleRouteSelection(routeItem, shift.id)}
          className={styles.routeHead}
        >
          <PinIcon />
          <Trans>Rutt</Trans>
        </button>
        <MedomaGridList
          selectionMode="single"
          aria-label={t`Besök i rutt`}
          items={visits}
          dragAndDropHooks={dragAndDropHooks}
          dependencies={[isExpanded, selectedItem, matchedItemIds]}
          className={clsx(
            styles.visitsGridList,
            disablePointerEventsForRouteDropZone &&
              styles.disablePointerEventsToNotInterfereWithRouteDropZone,
          )}
        >
          {(visit) => {
            const patientLabel =
              visit.patient.status !== patientStatusSchema.enum.deleted
                ? getPatientFullName(visit.patient.name)
                : "";
            const { start, end, isAnyTimeOfDay } = calculateVisitTimes(visit);
            const timeSpanLabel = isAnyTimeOfDay
              ? t(timeOfDayDictionary.Any.long)
              : start && end
                ? formattedTimeSpan(start, end)
                : "";
            const gridItemAriaLabel = [t`Hembesök`, patientLabel, timeSpanLabel]
              .filter(Boolean)
              .join(", ");

            return (
              <MedomaGridListItem
                id={visit.id}
                textValue={gridItemAriaLabel}
                onAction={() => {
                  toggleVisitSelection(visit, routeId, shift.id);
                }}
                className={clsx(
                  matchedItemIds.has(visit.id) && styles.matchHighlight,
                )}
              >
                <TimelineVisitTile
                  visit={visit}
                  shift={shift}
                  variant={isExpanded ? "default" : "compact"}
                  isSelected={isVisitSelected(visit)}
                />
              </MedomaGridListItem>
            );
          }}
        </MedomaGridList>
      </DropZone>
    </LoadingOverlay>
  );
};

export const ShiftRow = ({
  shiftWithContents: { shift, items },
  matchedItemIds,
  isExpanded,
}: {
  shiftWithContents: IShiftWithContents;
  matchedItemIds: Set<string>;
  isExpanded: boolean;
}) => {
  const { t } = useLingui();
  const name = shiftName(shift, {
    competenceFormat: "full",
    employeeNameFormat: "full",
  });
  const competence = shift.competence;
  const shiftType = shift.type;

  const { startDateTime, endDateTime } = shift;

  const isDeleted = shift.deleted;

  const isDropZoneEnlarged = useIsShiftDropZoneEnlarged({
    shiftId: shift.id,
    hasRoutes: items.some(isRoute),
  });

  return (
    <li
      className={clsx(
        styles.shiftRow,
        enlargedDropZone.shift,
        isDropZoneEnlarged && [
          styles.dropZoneEnlarged,
          enlargedDropZone.enlargedShift,
        ],
        styles[`variant-${isExpanded ? "default" : "compact"}`],
        isDeleted && styles.deleted,
      )}
    >
      {isDeleted ? (
        <div className={styles.deletedInfoText}>
          <Text element="span" size="small">
            <Trans>
              <strong>
                Detta arbetspass finns inte längre i schemaläggningssystemet.
              </strong>{" "}
              Flytta alla hembesök och övriga aktiviteter till ett annat
              arbetspass.
            </Trans>
          </Text>
        </div>
      ) : null}
      <div className={styles.shiftPerson}>
        <AllocateToShiftTile
          shift={shift}
          isDropZoneEnlarged={isDropZoneEnlarged}
        >
          <div className={styles.personGrid}>
            <div className={styles.competence}>
              <CompetenceChip
                competence={competence}
                state="neutral"
                content={t(medicalCompetenceDictionary[competence].short)}
                size="tiny"
              />
            </div>
            <div className={styles.name}>
              {isDeleted ? (
                <Text element="span" size="small">
                  {name}
                </Text>
              ) : (
                <Link
                  to={`../shifts/${shift.id}`}
                  color="neutral"
                  size="small"
                  weight="regular"
                  title={name}
                >
                  {name}
                </Link>
              )}
            </div>
            <div className={styles.time}>
              <ShiftTime
                size="small"
                startDateTime={startDateTime}
                endDateTime={endDateTime}
              />
            </div>
            {shift.team ? (
              <div className={styles.team}>
                <TeamBadge team={shift.team} size="small" />
              </div>
            ) : null}
            {unusualShiftTypes[shiftType] ? (
              <div className={styles.shiftTypeLabel}>
                {t(shiftTypesDictionary[shiftType])}
              </div>
            ) : null}
          </div>
        </AllocateToShiftTile>
      </div>
      <div className={styles.routesAndActivities}>
        {items.map(classifyShiftItem).map((shiftItem) => {
          switch (shiftItem.kind) {
            case shiftItemKindSchema.enum.route:
              return (
                <Route
                  routeItem={shiftItem.item}
                  isExpanded={isExpanded}
                  shift={shift}
                  matchedItemIds={matchedItemIds}
                  key={shiftItem.item.id}
                />
              );
            case shiftItemKindSchema.enum.group:
            case shiftItemKindSchema.enum.occurrence:
              return (
                <StandaloneActivityOccurrenceOrGroup
                  key={shiftItem.item.id}
                  occurrenceOrGroupItem={shiftItem.item}
                  isExpanded={isExpanded}
                  shift={shift}
                  isFilterMatch={matchedItemIds.has(shiftItem.item.id)}
                />
              );
            default:
              throw shiftItem satisfies never;
          }
        })}
      </div>
    </li>
  );
};
